import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useBooks } from '../context/BooksContext';
import StarRating from '../components/StarRating';
import { IoFlame, IoTrophy, IoTrendingUp, IoHeart, IoEye, IoBook, IoStar } from 'react-icons/io5';
import AdBanner from '../components/AdBanner';
import './Trending.css';


const GENRES = [
  { id: 'all', label: 'Tout', emoji: '🏆' },
  { id: 'romance', label: 'Romance', emoji: '💕' },
  { id: 'fantasy', label: 'Fantasy', emoji: '🗡️' },
  { id: 'thriller', label: 'Thriller', emoji: '🔍' },
  { id: 'scifi', label: 'Sci-Fi', emoji: '🚀' },
  { id: 'drama', label: 'Drame', emoji: '🎭' },
];

const TABS = [
  { id: 'hot', label: 'Hot', icon: IoFlame, description: 'Les plus populaires en ce moment' },
  { id: 'top', label: 'Top', icon: IoTrophy, description: 'Les mieux notés de tous les temps' },
  { id: 'rising', label: 'Rising', icon: IoTrendingUp, description: 'Les étoiles montantes' },
];

export default function Trending() {
  const { books: allBooks } = useBooks();
  const books = allBooks.filter(b => b.status !== 'draft');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('hot');
  const [selectedGenre, setSelectedGenre] = useState('all');

  const getAverageRating = (book) => {
    const ratings = book.ratings ? Object.values(book.ratings) : [];
    if (ratings.length === 0) return 0;
    return ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
  };

  const rankedBooks = useMemo(() => {
    let filtered = [...books];

    if (selectedGenre !== 'all') {
      filtered = filtered.filter(b => b.genre === selectedGenre);
    }

    switch (activeTab) {
      case 'hot':
        // Score = likes + views/10, pondéré par récence
        filtered.sort((a, b) => {
          const scoreA = a.likes + (a.views / 10);
          const scoreB = b.likes + (b.views / 10);
          return scoreB - scoreA;
        });
        break;
      case 'top':
        // Par note moyenne, puis par nombre de votes
        filtered.sort((a, b) => {
          const ratingA = getAverageRating(a);
          const ratingB = getAverageRating(b);
          if (ratingB !== ratingA) return ratingB - ratingA;
          return b.likes - a.likes;
        });
        break;
      case 'rising':
        // Les plus récents avec le meilleur ratio likes/vues
        filtered.sort((a, b) => {
          const ratioA = a.views > 0 ? a.likes / a.views : 0;
          const ratioB = b.views > 0 ? b.likes / b.views : 0;
          return ratioB - ratioA;
        });
        break;
      default:
        break;
    }

    return filtered.slice(0, 20);
  }, [books, activeTab, selectedGenre]);

  const activeTabInfo = TABS.find(t => t.id === activeTab);

  const genreLabels = {
    romance: 'Romance', fantasy: 'Fantasy', thriller: 'Thriller',
    scifi: 'Sci-Fi', drama: 'Drame', horror: 'Horreur',
  };

  const getMedalEmoji = (index) => {
    if (index === 0) return '🥇';
    if (index === 1) return '🥈';
    if (index === 2) return '🥉';
    return null;
  };

  return (
    <div className="page trending-page">
      <div className="trending-header">
        <h1 className="trending-title">Classements</h1>
        <p className="trending-subtitle">Les meilleurs livres de Plumly</p>
      </div>

      {/* Tabs */}
      <div className="trending-tabs">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <motion.button
              key={tab.id}
              className={'trending-tab ' + (activeTab === tab.id ? 'trending-tab-active' : '')}
              onClick={() => setActiveTab(tab.id)}
              whileTap={{ scale: 0.95 }}
            >
              <Icon />
              <span>{tab.label}</span>
            </motion.button>
          );
        })}
      </div>

      <p className="trending-description">{activeTabInfo.description}</p>

      {/* Genres */}
      <div className="trending-genres">
        {GENRES.map(genre => (
          <button
            key={genre.id}
            className={'explore-genre ' + (selectedGenre === genre.id ? 'explore-genre-active' : '')}
            onClick={() => setSelectedGenre(genre.id)}
          >
            <span>{genre.emoji}</span> {genre.label}
          </button>
        ))}
      </div>

      {/* Podium pour les 3 premiers */}
      {rankedBooks.length >= 3 && (
        <div className="trending-podium">
          {rankedBooks.slice(0, 3).map((book, i) => (
            <motion.div
              key={book.id}
              className={'trending-podium-item trending-podium-' + (i + 1)}
              onClick={() => navigate('/read/' + book.id)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ y: -4 }}
            >
              <span className="trending-podium-medal">{getMedalEmoji(i)}</span>
              <div className="trending-podium-cover" style={{ background: book.coverGradient }}>
                <div className="trending-podium-cover-inner">
                  <span className="trending-podium-rank">#{i + 1}</span>
                </div>
              </div>
              <h3 className="trending-podium-title">{book.title}</h3>
              <p className="trending-podium-author">{book.author}</p>
              <div className="trending-podium-stats">
                <span><IoHeart /> {book.likes}</span>
              </div>
            </motion.div>
          ))}
        </div>
      )}

{/* Pub après le podium */}
      <AdBanner slot="trending-top" format="horizontal" />

      {/* Liste classement */}
      <div className="trending-list">
        {rankedBooks.slice(3).map((book, i) => (
          <motion.div
            key={book.id}
            className="trending-list-item"
            onClick={() => navigate('/read/' + book.id)}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: (i + 3) * 0.05 }}
            whileHover={{ x: 4 }}
          >
            <span className="trending-list-rank">#{i + 4}</span>
            <div className="trending-list-cover" style={{ background: book.coverGradient }}>
              <IoBook style={{ color: 'white', fontSize: 18, opacity: 0.6 }} />
            </div>
            <div className="trending-list-info">
              <h3 className="trending-list-title">{book.title}</h3>
              <p className="trending-list-author">
                par <span onClick={(e) => { e.stopPropagation(); navigate('/author/' + book.authorId); }} style={{ textDecoration: 'underline', cursor: 'pointer' }}>{book.author}</span>
                {' · '}<span className={'tag tag-' + book.genre} style={{ fontSize: 10, padding: '2px 8px' }}>{genreLabels[book.genre] || book.genre}</span>
              </p>
              <StarRating bookId={book.id} ratings={book.ratings || {}} interactive={false} size="sm" />
            </div>
            <div className="trending-list-stats">
              <span className="trending-list-stat"><IoHeart /> {book.likes}</span>
              <span className="trending-list-stat"><IoEye /> {book.views.toLocaleString('fr-FR')}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {rankedBooks.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">🏆</div>
          <p className="empty-state-text">Aucun livre dans cette catégorie pour le moment</p>
        </div>
      )}

      {/* Pub bas de page */}
      <AdBanner slot="trending-bottom" format="horizontal" />
    </div>
  );
}